/**
 * `<ContactCreateRoute>` — `/w/:slug/contacts/new`.
 *
 * Dedicated create page (mirrors the Agents/KB `…/new` flow) instead of a
 * modal: `<PageShell>` + the shared `<ContactProfileForm>` in create mode.
 * Owns the create mutation; on success it routes to the new contact's
 * detail page. Cancel returns to the list.
 */

import { useWorkspace } from "@cometchat-ai/auth/client";
import { Button, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ContactProfileForm } from "../components/contacts/ContactProfileForm";
import { CheckTick, CloseX } from "../components/glyphs";
import { ChevronRight } from "../components/icons";
import { PageShell } from "../components/page-shell";
import { usePageBreadcrumb } from "../layouts/breadcrumb-trail";
import { ApiError } from "../lib/api/errors";
import { useContactsClient } from "../lib/api/use-contacts-client";
import type { CreateContactInput } from "../lib/api/types";

const FORM_ID = "contact-profile-form";

export function ContactCreateRoute() {
  const { t } = useTranslation("contacts");
  const { workspace } = useWorkspace();
  const client = useContactsClient();
  const qc = useQueryClient();
  const navigate = useNavigate();

  usePageBreadcrumb([{ label: t("create.title") }]);

  const [serverError, setServerError] = useState<{ code: string; message: string } | undefined>(
    undefined,
  );

  const base = `/w/${workspace.slug}/contacts`;

  const createMutation = useMutation({
    mutationFn: (input: CreateContactInput) => client.create(input),
    onSuccess: (created) => {
      toast.success(t("create.toast"));
      void qc.invalidateQueries({ queryKey: ["contacts", "list"] });
      navigate(`${base}/${created.id}`);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setServerError({ code: err.code, message: err.message });
      } else {
        toast.error(t("create.errorTaken"));
      }
    },
  });

  return (
    <PageShell
      className="p-0"
      data-testid="contact-create-route"
      title={
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(base)}
            className="text-base font-semibold tracking-[0.01em] text-foreground transition-colors hover:text-muted-foreground"
          >
            {t("nav.contacts")}
          </button>
          <ChevronRight
            className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)]"
            aria-hidden="true"
          />
          <span className="truncate text-base font-medium tracking-[0.01em] text-muted-foreground">
            {t("create.title")}
          </span>
        </div>
      }
    >
      <div className="overflow-auto px-6 py-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="rounded-2xl border border-border p-6">
            <ContactProfileForm
              id={FORM_ID}
              renderFooter={false}
              mode="create"
              busy={createMutation.isPending}
              serverError={serverError}
              onCancel={() => navigate(base)}
              onSubmitCreate={(input) => {
                setServerError(undefined);
                createMutation.mutate(input);
              }}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => navigate(base)}
              disabled={createMutation.isPending}
              className="h-8 min-w-[132px] justify-center gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              data-testid="contact-create-cancel"
            >
              <CloseX className="h-3.5 w-3.5" />
              {t("form.cancel")}
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              size="sm"
              disabled={createMutation.isPending}
              className="h-8 min-w-[132px] justify-center gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
              data-testid="contact-create-submit"
            >
              <CheckTick className="h-3.5 w-3.5" />
              {t("form.submitCreate")}
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
